import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
// Store
import { Store } from '@ngrx/store';
import { flatMap as _flatmap, get as _get, some as _some } from 'lodash';
import { Subject } from 'rxjs';
import { filter, flatMap, mergeMap, take, takeLast, takeUntil } from 'rxjs/operators';

import { BillAccountActions, BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import {
  AutomaticPayment,
  BillAccount,
  BillingPaymentPaths,
  DeletePreferencesActionPayload,
  DeletePreferencesRequest,
  DeliveryPreferences,
  ONLINE_BILLING,
  PaymentAccount,
  PaymentStatus,
  PcmAnalytics,
  PolicyList,
  RetrievePreferencePayload,
  ScheduledPayment,
  UpdateRegistrationRequest
} from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { ErrorLevels, ErrorObj, LoadingSpinnerService } from '@amfam/ui-kit';

import * as fromDetailCommonSelector from '../../+state/selectors';

@Component({
  selector: 'ds-new-bill-account-stop-managing',
  templateUrl: './new-bill-account-stop-managing.component.html',
  styleUrls: ['./new-bill-account-stop-managing.component.scss'],
  standalone: false
})
export class NewBillAccountStopManagingComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  billAccount: BillAccount;

  @Input()
  deletePreferenceRetry: boolean;

  @Input()
  unAssociatedAccount: PolicyList;

  scheduledPaymentsToRemove: ScheduledPayment[] = [];
  automaticPaymentsToCancel: AutomaticPayment[] = [];
  deletePaymentMethods: boolean;
  paymentAccounts: PaymentAccount[] = [];
  preferenceWillChange: boolean;
  updateRegistrationError = false;
  errorList: Array<ErrorObj> = [];
  stopManagingAnalytics = PcmAnalytics.stopManagingBillAccount;

  // Tracks whether to show the warning before the unregister action
  nextStep = false;

  stopManagingBtnContent: string;

  private emailAddress;
  private stop$: Subject<void> = new Subject<void>();
  private policyDescription: string[] = [];

  constructor(
    private store: Store,
    public roleService: ImpersonateRolesService,
    private featureFlagService: FeatureFlagService,
    private utilService: UtilService,
    private spinner: LoadingSpinnerService,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    this.stopManagingBtnContent = this.copyService.getCopy('billing.stopManagingButton');
    this.store
      .select(userQuery.selectUserState)
      .pipe(takeLast(1))
      .subscribe(currentUser => {
        this.emailAddress = _get(currentUser, 'emailAddress', '');
      });

    const emailCode = DeliveryPreferences.EMAIL.toUpperCase();
    // Unregistering sets to paper, so preference will change unless it is currently paper
    this.preferenceWillChange = _some(_get(this, 'billAccount.billingPreferences.preferences'), {
      preferenceDeliveryCode: emailCode
    });

    this.store
      .select(fromDetailCommonSelector.selectCombinedAccountState)
      .pipe(
        filter(state => !state.loading && !state.error),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        this.scheduledPaymentsToRemove = state.scheduledPayments
          .filter(payment =>
            _some(payment.billAccounts, {
              billAccountNumber: this.billAccount.billAccountNumber
            })
          )
          .filter(
            payment =>
              // Payments that are already authorized (same day credit/debit) cannot be removed
              payment.paymentStatus !== PaymentStatus.AUTHORIZED
          );
        this.automaticPaymentsToCancel = state.automaticPayments.filter(
          payments => payments.billAccountNumber === this.billAccount.billAccountNumber
        );

        const registeredHereBillAccounts: BillAccount[] = state.userRegisteredBillAccounts.filter(
          (billAccount: BillAccount) => !billAccount.registeredElsewhere
        );
        // We delete payment methods if we are unregistering the last bill account
        this.deletePaymentMethods =
          registeredHereBillAccounts.length === 1 &&
          _get(registeredHereBillAccounts[0], 'billAccountNumber') ===
            _get(this.billAccount, 'billAccountNumber');
        this.paymentAccounts = state?.paymentAccounts?.filter(x => x !== undefined);
      });

    this.policyDescription = _flatmap(
      _get(this, 'billAccount.policyList', []),
      policy => policy.riskDescriptionList
    );
  }

  ngOnChanges() {
    if (this.deletePreferenceRetry) {
      this.store.dispatch(
        BillAccountActions.billAccountDeletePreferenceResetRetry({
          billAccountNumber: this.billAccount.billAccountNumber
        })
      );
      this.deletePreference(true);
    }
  }

  stopManaging() {
    this.spinner.start({ blockActions: true });

    if (_get(this.billAccount, 'billingMethod') === ONLINE_BILLING) {
      this.unregister();
    } else {
      // We are deleting a preference for an unregistered account
      this.deletePreference();
    }
  }

  unregister() {
    // generate coorelation id
    const correlationId = this.utilService.generateId();

    // Build the payload
    const payload: UpdateRegistrationRequest = this.buildPayload(
      this.policyDescription,
      correlationId
    );

    // add subscription for this bill account with a matching registration correlation id
    this.store.dispatch(BillAccountActions.billAccountUpdateRegistrationSubmit(payload));

    // Subscribe to billAccounts store to get update billAccount with matching correlation id.
    this.store
      .select(BillAccountsSelectors.selectAllBillAccountsNotifications)
      .pipe(
        flatMap(billAccounts => billAccounts),
        filter(billAccount => billAccount && billAccount.correlationId === correlationId),
        take(1)
      )
      .subscribe(billAccount => {
        this.spinner.stop();
        if (billAccount.updatedRegistration) {
          // This should trigger a reload of the bill acount and this component would get garbage collected
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.CONFIRMATION_PATH]
            })
          );
        } else if (!billAccount.updateRegistrationPartialFailure) {
          // Partial failures are handled in bill-account-detail.component
          this.updateRegistrationError = true;
          this.errorList = this.showErrorMessage([
            this.copyService.get('billing', 'defaultBillingErrorMessage')
          ]);
        }
      });
  }
  ngOnDestroy() {
    this.store.dispatch(
      BillAccountActions.billAccountUpdateRegistrationInit(
        _get(this.billAccount, 'billAccountNumber')
      )
    );
    this.stop$.next();
    this.stop$.complete();
  }
  private buildPayload(
    policyDescription: string[],
    correlationId: string
  ): UpdateRegistrationRequest {
    const payload: UpdateRegistrationRequest = {
      billAccountNumber: this.billAccount.billAccountNumber,
      correlationId: correlationId,
      isUnregister: true,
      registrationData: {
        billAccountNumber: this.billAccount.billAccountNumber,
        policyNumber: _get(this.billAccount, 'policyList[0].policyNumber', ''),
        policyDescription: policyDescription,
        accountName: _get(this.billAccount, 'billingPreferences.accountNickName'),
        reminder: '0',
        associated: this.billAccount.associated,
        emailAddress: this.emailAddress,
        billingSystem: this.billAccount.billingSystem
      }
    };

    return payload;
  }

  private deletePreference(isRetry?: boolean) {
    const correlationId = this.utilService.generateId();
    const deletePreferencePayload: DeletePreferencesRequest = {
      deleteCorrelationId: correlationId,
      billAccountNumber: this.billAccount.billAccountNumber,
      policyDescription: this.policyDescription
    };
    const loadPreferencePayload: RetrievePreferencePayload = {
      billAccountNumber: this.billAccount.billAccountNumber,
      associated: this.billAccount.associated,
      billingMethod: this.billAccount.billingMethod
    };
    const payload: DeletePreferencesActionPayload = {
      billAccountNumber: this.billAccount.billAccountNumber,
      deletePreference: deletePreferencePayload,
      loadPreference: loadPreferencePayload
    };
    this.store.dispatch(BillAccountActions.billAccountDeletePreference(payload));

    // Get error case by subscribing to get matching correlation id with error
    this.store
      .select(BillAccountsSelectors.selectAllBillAccountsNotifications)
      .pipe(
        mergeMap(billAccounts => billAccounts),
        filter(
          billAccount =>
            billAccount &&
            billAccount.deleteCorrelationId === correlationId &&
            !!billAccount.deletePreferencesError
        ),
        take(1)
      )
      .subscribe(() => {
        this.spinner.stop();
        if (!isRetry) {
          // Retry case handled by modal in bill-account-detail
          this.updateRegistrationError = true;
          this.errorList = this.showErrorMessage([
            this.copyService.get('billing', 'defaultBillingErrorMessage')
          ]);
        }
      });
  }

  private showErrorMessage(
    errorMessageList: Array<string> = [],
    errorLevel?: ErrorLevels
  ): Array<ErrorObj> {
    let errorList: Array<ErrorObj> = [];
    errorMessageList.forEach((msg: string) => {
      if (!errorList.find(error => error.errorMessage === msg)) {
        errorList = [
          ...errorList,
          { errorMessage: msg, errorLevel: errorLevel || ErrorLevels.HIGH }
        ];
      }
    });
    return errorList;
  }
}
